import { Check } from 'lucide-react'
import * as React from 'react'

import { cn } from '../lib/utils'

// Rings the whole group, so keyboard focus reads as "inside a set" and the arrows move the choice.
// The group shares the tiles' radius, so the offset ring stays concentric with them.
const GROUP_FOCUS_RING =
  'has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-offset-4 has-[:focus-visible]:outline-pelorous-500 dark:has-[:focus-visible]:outline-pelorous-300'

export interface BudgetPickerOption<T extends string> {
  value: T
  /** What sits above the word — "$", "$$" or "$$$", or an icon. */
  symbol: React.ReactNode
  /** The level's word, already translated — e.g. "Lean". */
  label: string
}

export interface BudgetPickerProps<T extends string> {
  options: readonly BudgetPickerOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Accessible name for the group, already translated. */
  label: string
  className?: string
}

/**
 * Three radio tiles for a grocery budget: a symbol or icon above a word. The chosen tile tints green
 * and gains a check, so it never reads by colour alone. Keyboard focus rings the whole group.
 */
export function BudgetPicker<T extends string>({ options, value, onChange, label, className }: BudgetPickerProps<T>) {
  // Radios group by `name`, so each picker on a page needs its own.
  const groupName = React.useId()

  return (
    // The ring is outside `cn`: tailwind-merge reads `outline-[3px]` as clashing with `outline` and drops the style.
    <div role="radiogroup" aria-label={label} className={`${GROUP_FOCUS_RING} ${cn('grid grid-cols-3 gap-2 rounded-[14px]', className)}`}>
      {options.map((option) => {
        const selected = option.value === value
        return (
          <label
            key={option.value}
            className={cn(
              'flex min-h-16 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-[14px] border px-2 py-2.5 shadow-card transition-colors duration-200 motion-reduce:transition-none',
              selected
                ? 'border-brand-green-border bg-brand-green-tint text-brand-green-on-tint'
                : 'border-border bg-card text-muted-foreground hover:border-brand-green-border'
            )}>
            <input
              type="radio"
              name={groupName}
              value={option.value}
              checked={selected}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            <span
              aria-hidden="true"
              className={cn('font-mono text-[1.0625rem] font-medium tracking-[0.04em]', !selected && 'text-foreground')}>
              {option.symbol}
            </span>
            <span className="inline-flex items-center gap-1 text-[0.8125rem] font-medium">
              {selected && <Check className="size-3" strokeWidth={3} aria-hidden="true" />}
              {option.label}
            </span>
          </label>
        )
      })}
    </div>
  )
}
